import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom'; // Hook to get userId from URL
import './ClientBuild.css'; // Reuse existing styles

const PublicSurvey = () => {
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [userResponse, setUserResponse] = useState('');
  const [selectedOptions, setSelectedOptions] = useState([]); // For multiple-choice selections
  const [conversation, setConversation] = useState([]);
  const [message, setMessage] = useState([]);
  const [showSummary, setShowSummary] = useState(false);
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [surveyTitle, setSurveyTitle] = useState('');
  const [isSurveyCompleted, setIsSurveyCompleted] = useState(false);
  const [userName, setUserName] = useState('');
  const { userId } = useParams();
  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userResponse = await axios.get(`${apiUrl}/api/users/${userId}`);
        setUserName(`${userResponse.data.firstname} ${userResponse.data.lastname}`);

        const { data } = await axios.get(`${apiUrl}/api/public-survey/${userId}`);
        setQuestions(data);
        if (data.length > 0) {
          setCurrentQuestion(data[0]);
        }

        const { data: surveyTitleResponse } = await axios.get(`${apiUrl}/api/users/${userId}/survey-title`);
        setSurveyTitle(surveyTitleResponse.surveyTitle || 'Coaching Survey');
      } catch (error) {
        console.error('Failed to load public survey questions or user data:', error);
        setErrorMessage('Failed to load survey questions or user data');
      }
    };
    fetchUserData();
  }, [apiUrl, userId]);

  const handleUserResponse = () => {
    const nextIndex = currentQuestionIndex + 1;

    // Handle single and multiple-choice responses
    const response = currentQuestion.isMultipleChoice
      ? selectedOptions.join(', ') // Join selected options for multiple-choice
      : userResponse;

    const updatedConversation = [...conversation, { question: currentQuestion.questionText, response }];
    setConversation(updatedConversation);

    if (nextIndex < questions.length) {
      setCurrentQuestion(questions[nextIndex]);
      setCurrentQuestionIndex(nextIndex);
      setUserResponse('');
      setSelectedOptions([]);
    } else {
      setIsSurveyCompleted(true);
      setCurrentQuestion(null);
      handleFinishSession(updatedConversation);
    }
  };

  const handleCheckboxChange = (option) => {
    setSelectedOptions((prev) =>
      prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]
    );
  };

  const handleFinishSession = async (finalConversation) => {
    setLoading(true);
    try {
      const criteriaResponse = await axios.get(`${apiUrl}/api/users/${userId}/survey-criteria`);
      const surveyCriteria = criteriaResponse.data.customSurveyCriteria;

      const response = await axios.post(`${apiUrl}/api/openai/questions/summary`, {
        responses: finalConversation.map((conv) => `${conv.question} ${conv.response}`),
        surveyCriteria,
      });

      const generatedSummary = response.data.summary;
      setSummary(`Thank you for completing the survey. ${userName} will be reaching out to you shortly.`);
      await handleSendEmail(finalConversation, generatedSummary);

      setShowSummary(false);
    } catch (error) {
      console.error('Error generating the summary or sending the email:', error);
      setErrorMessage('Failed to generate the summary or send the email.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async (finalConversation, summary) => {
    try {
      await axios.post(`${apiUrl}/api/public-survey/send-email`, {
        userId,
        conversation: finalConversation,
        summary,
      });
      setMessage('Survey submitted successfully! Thank you for your time.');
    } catch (error) {
      console.error('Failed to send survey:', error);
      setErrorMessage('Failed to send the survey');
    }
  };

  return (
    <div className="client-build-container">
      <h1>{surveyTitle}</h1>
      {errorMessage && <p className="error-message">{errorMessage}</p>}
      {message && <p className="success-message">{message}</p>}
      {conversation.map((conv, index) => (
        <div key={index} className="conversation-item">
          <strong>{conv.question}</strong>
          <p>{conv.response}</p>
        </div>
      ))}
      {!showSummary && currentQuestion && (
        <div className="question-section">
          <p className="current-question">{currentQuestion.questionText}</p>
          {currentQuestion.isMultipleChoice ? (
            <div className="multiple-choice-options">
              {currentQuestion.options.map((option, index) => (
                <div key={index} className="checkbox-option">
                  <label>
                    <input
                      type="checkbox"
                      value={option}
                      checked={selectedOptions.includes(option)}
                      onChange={() => handleCheckboxChange(option)}
                    />
                    {option}
                  </label>
                </div>
              ))}
            </div>
          ) : (
            <textarea
              value={userResponse}
              onChange={(e) => setUserResponse(e.target.value)}
              rows="3"
              placeholder="Type your answer here..."
            />
          )}
          <div className="action-buttons">
            <button
              onClick={handleUserResponse}
              disabled={loading || (!userResponse.trim() && selectedOptions.length === 0) || isSurveyCompleted}
            >
              {loading ? 'Processing...' : 'Submit Response'}
            </button>
          </div>
        </div>
      )}
      {isSurveyCompleted && <p style={{ textAlign: 'center' }}>{summary}</p>}
    </div>
  );
};

export default PublicSurvey;
